import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { Layout } from '../components/Layout';
import { documentAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import {
  FileText,
  Upload,
  Loader2,
  Trash2,
  Download,
  Check,
  User,
  Clock,
} from 'lucide-react';
import { HelpTooltip } from '../components/HelpTooltip';

export default function Documents() {
  const { user, canManage } = useAuth();
  const { selectedLocationId, isAllLocations } = useLocation();
  const activeLocId = isAllLocations ? user?.restaurant_id : selectedLocationId;
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
  }, [user, activeLocId]);

  const fetchDocuments = async () => {
    if (!activeLocId) {
      setLoading(false);
      return;
    }

    try {
      const response = await documentAPI.getAll(activeLocId);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }

    setUploading(true);
    try {
      await documentAPI.upload(activeLocId, file);
      toast.success('Document uploaded!');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAcknowledge = async (docId) => {
    try {
      await documentAPI.acknowledge(docId);
      toast.success('Document acknowledged');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to acknowledge document');
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm('Delete this document?')) return;

    try {
      await documentAPI.delete(docId);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (docId) => {
    try {
      const res = await documentAPI.getDownloadUrl(docId);
      window.open(res.data.url, '_blank');
    } catch (error) {
      toast.error('Failed to get download link');
    }
  };

  const hasAcknowledged = (doc) => {
    return doc.acknowledged_by_user_ids?.includes(user?.id);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Documents
              {canManage && <HelpTooltip text="Securely store and share important company documents such as policies, contracts, training manuals, and internal files." learnMoreUrl="/help/documents" />}
            </h1>
            <p className="text-slate-500 mt-1">
              Important documents for your team
            </p>
          </div>

          {canManage && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleUpload}
                accept=".pdf"
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-document-btn"
              >
                {uploading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Upload PDF
              </Button>
            </>
          )}
        </div>

        {/* Documents List */}
        {documents.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12">
              <div className="text-center">
                <FileText className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-2 text-slate-500">No documents uploaded</p>
                {canManage && (
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-4"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Upload First Document
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {documents.map((doc) => {
              const acknowledged = hasAcknowledged(doc);
              
              return (
                <Card
                  key={doc.id}
                  className={`border-slate-200 ${!acknowledged ? 'border-l-4 border-l-amber-500' : ''}`}
                  data-testid={`document-${doc.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          acknowledged ? 'bg-green-100' : 'bg-slate-100'
                        }`}>
                          <FileText className={acknowledged ? 'text-green-600' : 'text-slate-600'} size={24} />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-900">{doc.filename}</h3>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              {doc.uploaded_by_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {formatDate(doc.created_at)}
                            </span>
                          </div>
                          <div className="mt-1">
                            <span className="text-xs text-slate-500">
                              {doc.acknowledged_by_user_ids?.length || 0} acknowledgments
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(doc.id)}
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>

                        {!acknowledged && (
                          <Button
                            size="sm"
                            onClick={() => handleAcknowledge(doc.id)}
                            className="bg-green-500 hover:bg-green-600"
                            data-testid={`acknowledge-doc-${doc.id}`}
                          >
                            <Check className="mr-1 h-4 w-4" />
                            Acknowledge
                          </Button>
                        )}

                        {acknowledged && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                            Acknowledged
                          </span>
                        )}

                        {canManage && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-slate-400 hover:text-red-500"
                            onClick={() => handleDelete(doc.id)}
                            data-testid={`delete-doc-${doc.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
